import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'src/api/axios';
import { NotificationManager } from 'react-notifications';
import { showModal } from 'src/redux/actions/modalActions';
import heart from 'src/assets/svg/heart.svg';
import heartPurpleFull from 'src/assets/svg/heart-purple-full.svg';

const FavoriteButton = ({ articleId, initialIsFavorited, onFavoriteChange, onUpdate, className }) => {
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const [isFavorited, setIsFavorited] = useState(initialIsFavorited);

    // Ažuriraj state kada se promeni initialIsFavorited prop
    useEffect(() => {
        setIsFavorited(initialIsFavorited);
    }, [initialIsFavorited]);

    const handleAddToFavorite = async () => {
        try {
            const response = await axios.post('/api/article/' + articleId + '/favorite', {
                article_id: articleId,
            }, {
                disableNotification: true
            });
            NotificationManager.success(response.data?.message || 'Article added to favorites');
            if (response.data.data) {
                setIsFavorited(true);
                if (onFavoriteChange) {
                    onFavoriteChange(true);
                }
                if (onUpdate) {
                    onUpdate(response.data.data);
                }
            }
        } catch (error) {
            // Prikaži error poruku jer smo koristili disableNotification: true
            NotificationManager.error(error.response?.data?.message || 'Error adding article to favorites');
        }
    };

    // Kada se korisnik prijavi, proveri da li postoji pending favorite za ovaj article
    useEffect(() => {
        const pendingArticleId = localStorage.getItem('pendingFavoriteArticleId');
        if (auth.isLoggedIn && pendingArticleId && parseInt(pendingArticleId) === articleId && !isFavorited) {
            // Automatski dodaj u favorite nakon prijave
            handleAddToFavorite();
            // Ukloni pending favorite iz localStorage
            localStorage.removeItem('pendingFavoriteArticleId');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.isLoggedIn, articleId, isFavorited]);

    const handleFavoriteClick = async () => {
        // Proveri da li je korisnik prijavljen
        if (!auth.isLoggedIn) {
            // Sačuvaj articleId u localStorage da se doda u favorite nakon prijave
            if (!isFavorited) {
                localStorage.setItem('pendingFavoriteArticleId', articleId.toString());
            }
            // Prikaži login modal
            dispatch(showModal('LOGIN_MODAL'));
            return;
        }

        // Ako je prijavljen, izvrši akciju
        if (isFavorited) {
            await handleRemoveFromFavorite();
        } else {
            await handleAddToFavorite();
        }
    };

    const handleRemoveFromFavorite = async () => {
        try {
            const response = await axios.delete('/api/article/' + articleId + '/favorite', {
                disableNotification: true
            });
            NotificationManager.success(response.data?.message || 'Article removed from favorites');
            if (response.data.data) {
                setIsFavorited(false);
                if (onFavoriteChange) {
                    onFavoriteChange(false);
                }
                if (onUpdate) {
                    onUpdate(response.data.data);
                }
            }
        } catch (error) {
            // Prikaži error poruku jer smo koristili disableNotification: true
            NotificationManager.error(error.response?.data?.message || 'Error removing article from favorites');
        }
    };

    const defaultClassName = 'w-8 h-8 sm:w-10 sm:h-10 min-w-8 sm:min-w-10 rounded-full flex items-center justify-center touch-manipulation';
    const buttonClassName = className || defaultClassName;

    return (
        <button 
            className={buttonClassName}
            onClick={handleFavoriteClick}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        >
            <img 
                className='w-5 h-5 sm:w-6 sm:h-6 m-auto' 
                src={isFavorited ? heartPurpleFull : heart} 
                alt={isFavorited ? 'Remove from favorites' : 'Add to favorites'} 
            />
        </button>
    );
};

FavoriteButton.propTypes = {
    articleId: PropTypes.number.isRequired,
    initialIsFavorited: PropTypes.bool,
    onFavoriteChange: PropTypes.func,
    onUpdate: PropTypes.func,
    className: PropTypes.string
};

export default FavoriteButton;

